import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, InputAdornment, Typography } from '@material-ui/core';
import MailOutlineOutlinedIcon from '@material-ui/icons/MailOutlineOutlined';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { FormField } from 'common/components/FormField';
import { TextField } from 'common/components/TextField';
import { Button } from 'common/components/buttons/Button';
import { IForgotPasswordMutationVariables } from 'graphql/types';

type TInitialValues = { email: string };

interface IProps {
    className?: string;
    loading?: boolean;
    onSubmit(values: IForgotPasswordMutationVariables): void;
}

const BaseForgotPasswordForm = ({ className, loading, onSubmit }: IProps) => {
    const { t } = useTranslation();
    const formik = useFormik({
        initialValues: {
            email: '',
        },
        validationSchema: Yup.object().shape({
            email: Yup.string()
                .email(t('validators.email.invalid'))
                .required(t('validators.email.required')),
        }),
        onSubmit: (values, { resetForm }) => {
            resetForm();

            onSubmit(values);
        },
    });

    const {
        values,
        errors: formikErrors,
        touched,
        handleChange,
        handleSubmit,
    } = formik;

    const getFieldError = (fieldName: keyof TInitialValues) => {
        if (touched[fieldName] && formikErrors[fieldName]) {
            return formikErrors[fieldName];
        }

        return '';
    };

    return (
        <form className={className}>
            <Typography color="primary" variant="h2">
                {t('account.forgotPassword.title')}
            </Typography>

            <Box className="description">
                <Typography color="textSecondary" variant="body2">
                    {t('account.forgotPassword.instruction')}
                </Typography>
            </Box>

            <FormField>
                <TextField
                    error={getFieldError('email')}
                    id="email"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                disablePointerEvents
                                position="start"
                            >
                                <MailOutlineOutlinedIcon fontSize="default" />
                            </InputAdornment>
                        ),
                    }}
                    label="e-mail"
                    type="email"
                    value={values.email}
                    variant="filled"
                    onChange={handleChange}
                />
            </FormField>

            <Box mt={6}>
                <Button
                    fullWidth
                    loading={loading}
                    onClick={() => handleSubmit()}
                >
                    {t('account.forgotPassword.buttonSubmit')}
                </Button>
            </Box>
        </form>
    );
};

export const ForgotPasswordForm = styled(BaseForgotPasswordForm)`
    .description {
        margin-bottom: ${({ theme }) => `${theme.spacing(10)}px`};
    }
`;
